import { Fragment, type FC, type ReactNode } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { TrackedDialog } from '@eneco/components/TrackedDialog/TrackedDialog';
import { Box, Divider, NavLink, NotificationBox, Ribbon, Stack, Text, TextLink } from '@eneco/sparky';
import type { FieldValues, UseFormHandleSubmit } from 'react-hook-form';

import { Header, Layout, NextButton, Overview } from '../../components';
import type { DetailsIconType, Price } from '../../types';

export interface ProductSummaryStepOverviewProductItem {
  id?: string;
  title: string;
  subtitle?: string;
  type: DetailsIconType;
  isBold?: boolean;
  hideIcon?: boolean;
  costsMonthly: Price;
  /** Explicit divider position/emphasis relative to this row; omitted rows have no divider. */
  divider?: 'high' | 'low' | 'medium' | 'none';
  /** Ribbon badge rendered above this row, e.g. a subscription discount banner. */
  badge?: { label?: string; content?: ReactNode };
}

export interface ProductSummaryStepOverviewSection {
  title?: string;
  /** Bold line rendered below the header, e.g. a hardware summary badge. */
  contentText?: string;
  products: ProductSummaryStepOverviewProductItem[];
  footer?: ReactNode;
}

export interface ProductSummaryStepTermsAndConditions {
  beforeContent?: string;
  afterContent?: string;
  dialogTitle?: string;
  dialogDescription?: ReactNode;
  triggerText?: string;
  links?: { url: string; text: string }[];
}

export interface ProductSummaryStepNotification {
  title?: string;
  text?: string;
  variant?: 'info' | 'success' | 'warning' | 'error' | 'chat';
}

export interface ProductSummaryStepProps {
  title?: string;
  text?: string;
  isLoading?: boolean;
  handleSubmit?: UseFormHandleSubmit<FieldValues>;
  isOfferError?: boolean;
  offerErrorTitle?: string;
  offerErrorText?: string;
  offerErrorVariant?: 'info' | 'success' | 'warning' | 'error' | 'chat';
  nextStepText?: string;
  isSubmitSuccessful?: boolean;
  /** The order overview cards — dongle/bundle-specific, supplied by the flow. One card is rendered per section. */
  orderOverviews: ProductSummaryStepOverviewSection[];
  /** Sales conditions text + dialog, shown below the cards. */
  termsAndConditions?: ProductSummaryStepTermsAndConditions;
  /** Extra notifications shown below the cards, e.g. based on the customer's selection. */
  notifications?: ProductSummaryStepNotification[];
}

/**
 * Presentational shell shared by flows whose overview page is "order summary + next button"
 * (dongle, bundle). Holds no data-fetching or flow-context logic itself; the flow keeps that
 * and supplies its order overview and notifications as props.
 */
export const ProductSummaryStep: FC<ProductSummaryStepProps> = ({
  title,
  text,
  isLoading,
  handleSubmit,
  isOfferError,
  offerErrorTitle,
  offerErrorText,
  offerErrorVariant,
  nextStepText,
  isSubmitSuccessful,
  orderOverviews,
  termsAndConditions,
  notifications,
}) => (
  <Layout.Content variant="B" isLoading={isLoading} handleSubmit={handleSubmit}>
    <Header>
      <Header.Title>{title}</Header.Title>
      {text && <Header.Text>{text}</Header.Text>}
    </Header>
    {isOfferError ? (
      <NotificationBox isAlert title={offerErrorTitle} text={offerErrorText} variant={offerErrorVariant} />
    ) : (
      <>
        <Stack direction="column" gap="6">
          {orderOverviews.map((orderOverview, sectionIndex) => (
            <Overview key={sectionIndex}>
              {orderOverview.title && <Overview.Header isBold>{orderOverview.title}</Overview.Header>}

              {orderOverview.contentText && (
                <>
                  <Overview.Content isBold>{orderOverview.contentText}</Overview.Content>
                  <Box paddingX="6">
                    <Divider />
                  </Box>
                </>
              )}

              {orderOverview.products.map((product, index) => (
                <Fragment key={product.id ?? index}>
                  {product.badge && (
                    <Box paddingX="6">
                      {product.badge.label && (
                        <Box>
                          <Text weight="bold" size="BodyM">
                            {product.badge.label}
                          </Text>
                        </Box>
                      )}
                      {product.badge.content && (
                        <Box paddingY="2" borderRadius="round" overflow="hidden">
                          <Ribbon emphasis="high" size="small">
                            {product.badge.content}
                          </Ribbon>
                        </Box>
                      )}
                    </Box>
                  )}

                  {product.divider === 'medium' && (
                    <Box paddingX="6">
                      <Divider emphasis="medium" />
                    </Box>
                  )}

                  <Overview.Product
                    title={product.title}
                    subtitle={product.subtitle}
                    type={product.type}
                    isBold={product.isBold}
                    hideIcon={product.hideIcon}
                    hideDivider
                    costsMonthly={product.costsMonthly}
                  />

                  {product.divider === 'high' && (
                    <Box paddingX="6">
                      <Divider emphasis="high" />
                    </Box>
                  )}
                  {product.divider === 'low' && (
                    <Box paddingY="2">
                      <Divider emphasis="low" />
                    </Box>
                  )}
                </Fragment>
              ))}

              {orderOverview.footer && <Overview.Footer>{orderOverview.footer}</Overview.Footer>}
            </Overview>
          ))}
        </Stack>

        {termsAndConditions && (
          <Box>
            <RichText html={termsAndConditions.beforeContent} />
            <TrackedDialog
              title={termsAndConditions.dialogTitle}
              description={termsAndConditions.dialogDescription}
              trigger={<TextLink>{termsAndConditions.triggerText}</TextLink>}>
              <Stack direction="column" gap="2">
                {termsAndConditions.links?.map(link => (
                  <NavLink key={link.url} href={link.url} target="_blank">
                    {link.text}
                  </NavLink>
                ))}
              </Stack>
            </TrackedDialog>
            <RichText html={termsAndConditions.afterContent} />
          </Box>
        )}

        {notifications?.map((notification, index) => (
          <NotificationBox
            key={index}
            isAlert
            title={notification.title}
            text={notification.text}
            variant={notification.variant}
          />
        ))}

        <Stack direction={{ initial: 'column', md: 'row' }}>
          <NextButton isLoading={isSubmitSuccessful}>{nextStepText}</NextButton>
        </Stack>
      </>
    )}
  </Layout.Content>
);
